import { Skeleton } from "@/components/ui/skeleton";
import { Box, Flex, SimpleGrid, Stack } from "@chakra-ui/react";

// Shared loading skeletons for the public agency experience (profile, blog post,
// form). Every loading layer for a given page — the custom-domain resolve, the
// lazy-chunk Suspense fallback, and the page's own data fetch — renders the same
// skeleton, so a page load reads as one continuous placeholder instead of a
// sequence of differently-shaped ones flashing past.

// Tints the shine shimmer for placeholders sitting on the dark gray.900 hero,
// where the default light skeleton colors would be invisible.
const DARK_SHIMMER = { "--start-color": "rgba(255,255,255,0.06)", "--end-color": "rgba(255,255,255,0.16)" };

// Mirrors the profile's real structure: a dark hero masthead over white section
// cards, so content swaps in without a light-to-dark flash or a layout shift.
export const ProfileSkeleton = ({ showBackLink }) => (
  <Flex direction="column" gap={{ base: 8, md: 12 }} maxW="1120px" mx="auto" w="full">
    {showBackLink && <Skeleton h="28px" w="160px" borderRadius="full" />}

    {/* Hero: dark surface, content pinned to the bottom like the real masthead */}
    <Flex direction="column" justify="flex-end" gap={5} bg="gray.900" borderRadius="xl" minH={{ base: "460px", md: "560px" }} p={{ base: 6, md: 12 }}>
      <Skeleton css={DARK_SHIMMER} w={{ base: "52px", md: "60px" }} h={{ base: "52px", md: "60px" }} borderRadius="lg" />
      <Skeleton css={DARK_SHIMMER} h={{ base: "40px", md: "52px" }} w={{ base: "85%", md: "60%" }} maxW="560px" borderRadius="md" />
      <Stack gap={2.5} maxW="560px" w="full">
        <Skeleton css={DARK_SHIMMER} h="16px" w="90%" borderRadius="sm" />
        <Skeleton css={DARK_SHIMMER} h="16px" w="55%" borderRadius="sm" />
      </Stack>
      <Skeleton css={DARK_SHIMMER} h="48px" w="208px" borderRadius="lg" mt={1} />
      <Skeleton css={DARK_SHIMMER} h="14px" w="260px" borderRadius="sm" />
    </Flex>

    {/* About: eyebrow + heading + prose + credentials grid */}
    <Box bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200" p={{ base: 6, md: 10 }}>
      <Skeleton h="12px" w="100px" borderRadius="full" mb={4} />
      <Skeleton h="28px" w={{ base: "70%", md: "320px" }} borderRadius="md" mb={6} />
      <Stack gap={2.5} maxW="680px" mb={8}>
        <Skeleton h="14px" w="full" borderRadius="sm" />
        <Skeleton h="14px" w="95%" borderRadius="sm" />
        <Skeleton h="14px" w="78%" borderRadius="sm" />
      </Stack>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={6} pt={8} borderTopWidth="1px" borderColor="gray.100">
        {[0, 1, 2].map((col) => (
          <Box key={col}>
            <Skeleton h="11px" w="78px" borderRadius="full" mb={3} />
            <Flex gap={1.5} flexWrap="wrap">
              {["58px", "82px", "50px"].map((w) => (
                <Skeleton key={w} h="24px" w={w} borderRadius="full" />
              ))}
            </Flex>
          </Box>
        ))}
      </SimpleGrid>
    </Box>

    {/* Destinations: eyebrow + heading + tile grid */}
    <Box bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200" p={{ base: 6, md: 10 }}>
      <Skeleton h="12px" w="100px" borderRadius="full" mb={4} />
      <Skeleton h="28px" w={{ base: "60%", md: "240px" }} borderRadius="md" mb={6} />
      <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={3}>
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} h="62px" borderRadius="lg" />
        ))}
      </SimpleGrid>
    </Box>
  </Flex>
);

// Mirrors the blog post layout: a compact dark hero masthead, then the article
// card (cover image, title, byline, body). The hero space is reserved here
// because the agency profile that powers it is fetched separately from the post,
// so without it the masthead would pop in late.
export const BlogSkeleton = () => (
  <Flex direction="column" gap={{ base: 8, md: 10 }} maxW="1120px" mx="auto" w="full">
    {/* Compact dark hero masthead */}
    <Flex direction="column" justify="flex-end" bg="gray.900" borderRadius="xl" minH={{ base: "180px", md: "220px" }} p={{ base: 6, md: 8 }}>
      <Flex align="center" gap={3.5}>
        <Skeleton css={DARK_SHIMMER} w={{ base: "48px", md: "56px" }} h={{ base: "48px", md: "56px" }} borderRadius="lg" />
        <Skeleton css={DARK_SHIMMER} h={{ base: "22px", md: "26px" }} w={{ base: "180px", md: "240px" }} borderRadius="md" />
      </Flex>
    </Flex>

    {/* Article card */}
    <Box bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200" overflow="hidden">
      <Skeleton h={{ base: "220px", md: "340px" }} w="full" />
      <Flex direction="column" p={{ base: 6, md: 10 }}>
        <Skeleton h={{ base: "28px", md: "34px" }} w="85%" borderRadius="md" mb={5} />
        <Flex align="center" gap={3} mb={6}>
          <Skeleton w="32px" h="32px" borderRadius="full" />
          <Stack gap={1.5}>
            <Skeleton h="13px" w="120px" borderRadius="sm" />
            <Skeleton h="11px" w="90px" borderRadius="sm" />
          </Stack>
        </Flex>
        <Stack gap={3}>
          {["100%", "97%", "92%", "98%", "85%", "94%", "70%"].map((w) => (
            <Skeleton key={w} h="14px" w={w} borderRadius="sm" />
          ))}
        </Stack>
      </Flex>
    </Box>
  </Flex>
);

// Mirrors the loaded form's card layout so content swaps in without a shift.
export const FormSkeleton = () => (
  <Flex justify="center" py={8} px={4}>
    <Box maxW="1120px" w="full">
      <Box bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200" px={{ base: 6, md: 8 }} py={{ base: 6, md: 8 }}>
        <Skeleton h="28px" w={{ base: "70%", md: "55%" }} borderRadius="md" mb={2} />
        <Stack gap={2} mb={6}>
          <Skeleton h="14px" w="full" borderRadius="sm" />
          <Skeleton h="14px" w="80%" borderRadius="sm" />
        </Stack>
        <Flex direction="column" gap={8}>
          <Flex gap={4} direction={{ base: "column", md: "row" }}>
            {[0, 1].map((i) => (
              <Box key={i} flex={1}>
                <Skeleton h="13px" w="90px" borderRadius="sm" mb={2} />
                <Skeleton h="42px" w="full" borderRadius="xl" />
              </Box>
            ))}
          </Flex>
          {["140px", "170px", "120px"].map((labelW) => (
            <Box key={labelW}>
              <Skeleton h="13px" w={labelW} borderRadius="sm" mb={2} />
              <Skeleton h="42px" w="full" borderRadius="xl" />
            </Box>
          ))}
          <Skeleton h="40px" w="120px" borderRadius="lg" mt={2} />
        </Flex>
      </Box>
    </Box>
  </Flex>
);
